import { useVirtualizer } from '@tanstack/react-virtual'
import { usePathname } from 'next/navigation'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { CopilotSession } from 'sprites-types/api/v2/copilot'
import { ThreadDropdownMenu } from './ThreadDropdownMenu'
import { ThreadItem } from './ThreadItem'
import { useThreadOperations } from './useThreadOperations'

type ThreadListProps = {
  sessions: CopilotSession[]
  searchQuery: string
  hasMore: boolean
  isFetching: boolean
  isLoading: boolean
  onLoadMore: () => void
}

export const ThreadList = memo(function ThreadList({
  sessions,
  searchQuery,
  hasMore,
  isFetching,
  isLoading,
  onLoadMore,
}: ThreadListProps) {
  const pathname = usePathname()
  const scrollRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const [isMounted, setIsMounted] = useState(false)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const { handleToggleBookmark, handleDeleteSession, handleRenameSession } = useThreadOperations()

  const virtualizer = useVirtualizer({
    count: sessions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 80,
    overscan: 5,
  })

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Update menu position when it opens
  useEffect(() => {
    if (openMenuId) {
      const button = menuButtonRefs.current.get(openMenuId)
      if (button) {
        const rect = button.getBoundingClientRect()
        setMenuPosition({
          top: rect.bottom + 4,
          left: rect.left,
        })
      }
    }
  }, [openMenuId])

  // Load more on scroll
  useEffect(() => {
    if (!scrollRef.current || searchQuery.trim().length > 0 || !hasMore || isFetching) return

    const handleScroll = () => {
      const scrollElement = scrollRef.current
      if (!scrollElement) return

      const { scrollTop, scrollHeight, clientHeight } = scrollElement
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

      if (scrollPercentage > 0.8) {
        onLoadMore()
      }
    }

    const scrollElement = scrollRef.current
    scrollElement.addEventListener('scroll', handleScroll)
    return () => scrollElement.removeEventListener('scroll', handleScroll)
  }, [hasMore, isFetching, searchQuery, onLoadMore])

  const handleStartRename = useCallback((sessionId: string, currentName: string) => {
    setEditingSessionId(sessionId)
    setEditingName(currentName)
    setOpenMenuId(null)
  }, [])

  const handleRenameSubmit = useCallback(
    async (sessionId: string) => {
      const success = await handleRenameSession(sessionId, editingName)
      if (success) {
        setEditingSessionId(null)
        setEditingName('')
      }
    },
    [handleRenameSession, editingName]
  )

  const handleCancelRename = useCallback(() => {
    setEditingSessionId(null)
    setEditingName('')
  }, [])

  const handleMenuClick = useCallback(
    (sessionId: string) => {
      setOpenMenuId(openMenuId === sessionId ? null : sessionId)
    },
    [openMenuId]
  )

  const handleDelete = useCallback(
    async (sessionId: string) => {
      await handleDeleteSession(sessionId)
      setOpenMenuId(null)
    },
    [handleDeleteSession]
  )

  if (!isMounted || sessions.length === 0) {
    if (isLoading) {
      return null
    }
    return (
      <div className="text-ui-secondary px-3 py-4 text-center text-sm">
        {searchQuery ? 'No chats found' : 'No chats yet'}
      </div>
    )
  }

  const openMenuSession = sessions.find((s) => s.id === openMenuId)

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-2 py-2">
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const session = sessions[virtualItem.index]
            return (
              <div
                key={session.id}
                className="group absolute left-0 top-0 w-full"
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <ThreadItem
                  id={session.id}
                  title={session.threadSummary || 'Untitled'}
                  updatedAt={session.updatedAt}
                  isBookmarked={session.isBookmarked ?? false}
                  isActive={pathname.includes(session.id)}
                  isEditing={editingSessionId === session.id}
                  editingName={editingName}
                  onEditingNameChange={setEditingName}
                  onToggleFavorite={() =>
                    handleToggleBookmark(session.id, session.isBookmarked ?? false)
                  }
                  onMenuClick={() => handleMenuClick(session.id)}
                  onRenameSubmit={() => handleRenameSubmit(session.id)}
                  onCancelRename={handleCancelRename}
                  menuButtonRef={(el) => {
                    if (el) {
                      menuButtonRefs.current.set(session.id, el)
                    }
                  }}
                />
              </div>
            )
          })}
        </div>
      </div>

      {openMenuSession && (
        <ThreadDropdownMenu
          isOpen={!!openMenuId}
          position={menuPosition}
          isBookmarked={openMenuSession.isBookmarked ?? false}
          onToggleFavorite={() => {
            handleToggleBookmark(openMenuId!, openMenuSession.isBookmarked ?? false)
            setOpenMenuId(null)
          }}
          onRename={() => {
            handleStartRename(openMenuId!, openMenuSession.threadSummary || openMenuId!)
          }}
          onDelete={() => handleDelete(openMenuId!)}
          menuRef={menuRef}
        />
      )}
    </>
  )
})
